import { Box, Typography, Button, ButtonGroup } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import PhoneInput from './PhoneInput';
import LoadingSpinner from './LoadingSpinner';
import { LoadingButton } from '@mui/lab';

type PwResetFormProps = {
  phoneNumber: string;
  setPhoneNumber: (phoneNumber: string) => void;
  onSmsSubmit: () => Promise<void>;
  onWpSubmit: () => Promise<void>;
};

export default function PwResetForm({
  phoneNumber,
  setPhoneNumber,
  onSmsSubmit,
  onWpSubmit,
}: PwResetFormProps) {
  const nav = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [method, setMethod] = useState<'sms' | 'wp'>('sms');

  const handleChange = (phone: string) => {
    if (phone) {
      setPhoneNumber(phone?.replace('+', ''));
    }
  };

  const handleSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      if (method === 'sms') {
        await onSmsSubmit();
      } else {
        await onWpSubmit();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.keyCode === 13) {
      handleSubmit(e);
    }
  };

  return (
    <Box>
      {loading && <LoadingSpinner loadingText={t('loginPage.submitting')} />}
      <form onSubmit={handleSubmit}>
        <Typography sx={{ fontSize: 14, textAlign: 'center' }}>
          {t('pwResetForm.pwResetLabel')}
        </Typography>

        <Box sx={{ my: 1.5 }}>
          <PhoneInput
            name="phoneNumber"
            value={phoneNumber}
            onChange={handleChange}
            onKeyDown={handleKeyPress}
            required
          />
        </Box>
        <ButtonGroup variant="outlined" fullWidth>
          <Button
            onClick={() => setMethod('sms')}
            variant={method === 'sms' ? 'contained' : 'outlined'}
          >
            {t('pwResetForm.selectSmsBtn')}
          </Button>
          <Button
            onClick={() => setMethod('wp')}
            variant={method === 'wp' ? 'contained' : 'outlined'}
          >
            {t('pwResetForm.selectWpBtn')}
          </Button>
        </ButtonGroup>
        <LoadingButton
          variant="contained"
          type="submit"
          color="primary"
          sx={{ mt: 3, height: 48, width: '100%' }}
          disabled={loading}
          loading={loading}
        >
          {method === 'sms'
            ? t('pwResetForm.submitSmsBtn')
            : t('pwResetForm.submitWpBtn')}
        </LoadingButton>
        <Button
          color="secondary"
          sx={{ mt: 2, width: '100%' }}
          variant="contained"
          onClick={() => nav('/login')}
        >
          {t('pwResetForm.pwResetLoginButton')}
        </Button>
      </form>
    </Box>
  );
}
